// src/components/AccountEdit.tsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    InputLabel,
    FormControl,
    Select, MenuItem, Autocomplete, Stack,
} from '@mui/material';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import Diversity3RoundedIcon from '@mui/icons-material/Diversity3Rounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { IAccount } from "../Interfaces/IAccount";
import EditableProfilePicture from "./EditableProfilePicture";
import AccountsField from "./AccountField";
import {useGetAccountStatus} from "../hooks/accountStatus";
import { AccountGroupView } from './AccountGroupView';
import { ICustomeGroup } from '../Interfaces/ICustomeGroup';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import {checkPermission, getCurrentUserId} from "../utils/permission";
import { useAlert } from '../core/AlertContext';
import SectionCard from '../core/SectionCard';
import { GREEN_900, sectionCardSx, fieldSx, iconCircleSx } from '../core/pvtaStyles';

interface AccountEditProps {
    initialAccount: IAccount;
    allAccounts: IAccount[];
    groupArray: ICustomeGroup[];
    updateAccount: UseMutateAsyncFunction<IAccount, Error, IAccount>;
    isPending: boolean;
    refetchUserArray: () => void;
}

const AccountEdit: React.FC<AccountEditProps> = ({ initialAccount, allAccounts, groupArray, updateAccount,isPending,refetchUserArray }) => {
    const [account, setAccount] = useState<IAccount>(initialAccount);
    const MAX_AVATAR_BYTES = 3 * 1024 * 1024;

    const { showAlert } = useAlert();

    const initialSelectedLeibbursche = allAccounts.filter(acc =>
        initialAccount.leibbursche_von?.includes(acc.id)
    );
    const [selectedLeibbursche, setSelectedLeibbursche] = useState<IAccount[]>(initialSelectedLeibbursche);

    const {statusArray} = useGetAccountStatus();

    const canEditAccount = (targetId?: number | null) => {
        return checkPermission(1, 2) || getCurrentUserId() === Number(targetId);
    };

    useEffect(() => {
        setAccount(prev => ({
            ...prev,
            leibbursche_von: selectedLeibbursche.map(acc => acc.id)
        }));
    }, [selectedLeibbursche]);

    ////////////////////////////// EMAIL VALIDATION //////////////////////////////
    const validateEmail = (email: string): boolean => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const [emailError, setEmailError] = useState("");

    ////////////////////////////// URL VALIDATION //////////////////////////////
    const validateURL = (url: string): boolean => {
        // Erlaube leere Felder (optional) oder prüfe auf ein gültiges URL-Format
        if (url.trim() === "") return true;
        const pattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
        return pattern.test(url);
    };
    const [urlErrors, setUrlErrors] = useState<{ [key: string]: string }>({});
    const urlFields = ["instagram", "facebook", "linkedin", "xing", "webseite"];


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // Update den Account-State
        setAccount(prev => ({
            ...prev,
            [name]: value,
        }));
        // Wenn es sich um ein URL-Feld handelt, validieren wir es
        if (urlFields.includes(name)) {
            if (!validateURL(value)) {
                setUrlErrors(prev => ({ ...prev, [name]: "Bitte gib eine gültige URL ein." }));
            } else {
                setUrlErrors(prev => ({ ...prev, [name]: "" }));
            }
        }
        if (name === "email") {
            if (!validateEmail(value)) {
                setEmailError("Bitte gib eine gültige E-Mail-Adresse ein.");
            } else {
                setEmailError("");
            }
        }
    };

    useEffect(() => {
        if (
            account.verstorben === "" ||
            account.eintritt === "" ||
            account.geburtstag === ""
        ) {
            setAccount(prev => ({
                ...prev,
                verstorben: prev.verstorben === "" ? null : prev.verstorben,
                eintritt: prev.eintritt === "" ? null : prev.eintritt,
                geburtstag: prev.geburtstag === "" ? null : prev.geburtstag,
            }));
        }
    }, [account.verstorben, account.eintritt, account.geburtstag]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            if (file.size > MAX_AVATAR_BYTES) {
                showAlert({ severity: 'error', text: 'Profilbild darf maximal 3 MB groß sein.' });
                e.target.value = '';
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                const base64Data = result.split(',')[1];
                setAccount(prev => ({
                    ...prev,
                    profile_picture: base64Data,
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            if(account.id < 0)
            {

            }
            else {
                await updateAccount(account);
            }

            showAlert({ text: 'Account erfolgreich aktualisiert.' });
        } catch (error) {
            showAlert({ text: 'Fehler beim Aktualisieren des Accounts.', severity: 'error' });
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 760,
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            {/* Profilbild prominent & zentriert */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pt: 1,
                    pb: 1,
                }}
            >
                <EditableProfilePicture account={account} onFileChange={handleFileChange} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Max. Dateigröße: 3 MB (JPG/PNG).
                </Typography>
            </Box>

            {/* Rollen & Funktionen als eigene Card */}
            <Box sx={{ ...sectionCardSx, p: { xs: 2, md: 2.5 } }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Box sx={iconCircleSx}>
                        <WorkspacePremiumOutlinedIcon fontSize="small" />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: GREEN_900, fontSize: 16 }}>
                        Rollen &amp; Funktionen
                    </Typography>
                </Stack>
                <AccountGroupView
                    account={account}
                    setAccount={setAccount}
                    groupArray={groupArray}
                />
            </Box>

            <form onSubmit={handleSubmit} id="account-edit-form">
                <Stack spacing={2}>
                    {/* 1. Persönliche Daten */}
                    <SectionCard collapsible number={1} title="Persönliche Daten" icon={<PersonOutlineRoundedIcon fontSize="small" />}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Vorname"
                                    name="first_name"
                                    value={account.first_name || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Nachname"
                                    name="last_name"
                                    value={account.last_name || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    type="date"
                                    label="Geburtstag"
                                    name="geburtstag"
                                    value={account.geburtstag || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    {/* 2. Kontakt */}
                    <SectionCard collapsible number={2} title="Kontakt" icon={<MailOutlineRoundedIcon fontSize="small" />}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Email"
                                    name="email"
                                    value={account.email}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                    error={Boolean(emailError)}
                                    helperText={emailError}
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Telefon"
                                    name="telefon"
                                    value={account.telefon || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Mobil"
                                    name="mobil"
                                    value={account.mobil || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    {/* 3. Adresse */}
                    <SectionCard collapsible number={3} title="Adresse" icon={<PlaceOutlinedIcon fontSize="small" />}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Strasse"
                                    name="strasse"
                                    value={account.strasse || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Hausnummer"
                                    name="hausnummer"
                                    value={account.hausnummer || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="PLZ"
                                    name="plz"
                                    value={account.plz || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Stadt"
                                    name="stadt"
                                    value={account.stadt || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Land"
                                    name="land"
                                    value={account.land || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    {/* 4. Verbindung & Rollen */}
                    <SectionCard
                        collapsible
                        number={4}
                        title="Verbindung & Rollen"
                        icon={<Diversity3RoundedIcon fontSize="small" />}
                        defaultExpanded={false}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth sx={fieldSx}>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        label="Status"
                                        name="status"
                                        value={account.status || ''}

                                        onChange={(e) => {
                                            const statusId = Number(e.target.value);
                                            setAccount(prev => ({ ...prev, status: statusId }));
                                        }}
                                    >
                                        {statusArray.map((status) => (
                                            <MenuItem key={status.id} value={status.id}>
                                                {status.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    type="date"
                                    label="Eintritt"
                                    name="eintritt"
                                    value={account.eintritt || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    type="date"
                                    label="Verstorben"
                                    name="verstorben"
                                    value={account.verstorben || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    fullWidth
                                    options={allAccounts}
                                    getOptionLabel={opt =>
                                        (`${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.username)
                                    }
                                    value={allAccounts.find(u => u.id === account.leibfux_von) || null}
                                    onChange={(_, sel) =>
                                        setAccount(prev => ({
                                            ...prev,
                                            leibfux_von: sel ? sel.id : null
                                        }))
                                    }
                                    disabled={!canEditAccount(account.id)}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Leibfux von"
                                            placeholder="Wähle Bundesbruder aus"
                                            variant="outlined"
                                            sx={fieldSx}
                                        />
                                    )}
                                    readOnly={!canEditAccount(account.id)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <AccountsField
                                    allAccounts={allAccounts}
                                    selectedAccounts={selectedLeibbursche}
                                    onChange={(newSelected: IAccount[]) => setSelectedLeibbursche(newSelected)}
                                    label="Leibfüx(e)"
                                    readOnly={!canEditAccount(account.id)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Biernamen"
                                    name="biernamen"
                                    value={account.biernamen || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Ämter"
                                    name="aemter"
                                    value={account.aemter || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    {/* 5. Weitere Angaben */}
                    <SectionCard
                        collapsible
                        number={5}
                        title="Weitere Angaben"
                        icon={<InfoOutlinedIcon fontSize="small" />}
                        defaultExpanded={false}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Beruf"
                                    name="beruf"
                                    value={account.beruf || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Arbeitgeber"
                                    name="arbeitgeber"
                                    value={account.arbeitgeber || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Webseite"
                                    name="webseite"
                                    value={account.webseite || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    error={Boolean(urlErrors.webseite)}
                                    helperText={urlErrors.webseite}
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Xing"
                                    name="xing"
                                    value={account.xing || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    error={Boolean(urlErrors.xing)}
                                    helperText={urlErrors.xing}
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="LinkedIn"
                                    name="linkedin"
                                    value={account.linkedin || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    error={Boolean(urlErrors.linkedin)}
                                    helperText={urlErrors.linkedin}
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Facebook"
                                    name="facebook"
                                    value={account.facebook || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    error={Boolean(urlErrors.facebook)}
                                    helperText={urlErrors.facebook}
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Instagram"
                                    name="instagram"
                                    value={account.instagram || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    error={Boolean(urlErrors.instagram)}
                                    helperText={urlErrors.instagram}
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            readOnly: !canEditAccount(account.id),
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Stack>
            </form>
        </Box>
    );
};

export default AccountEdit;
